/**
 * Iter79.6: AI Benchmark Card — Vergleicht alle 3 Vision-LLM-Provider
 * (OpenAI / Anthropic / Gemini) parallel auf demselben Beleg.
 */
import React, { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  FlaskConical,
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  Trophy,
} from "lucide-react";

const FIELD_LABELS = {
  merchant_name: "Lieferant",
  transaction_date: "Datum",
  total_amount: "Betrag",
  tax_amount: "MWST-Betrag",
  tax_rate: "MWST-Satz",
  currency: "Währung",
};

const PROVIDER_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  gemini: "Gemini",
};

function fmtCell(value) {
  if (value === null || value === undefined || value === "") return <span className="text-slate-400 italic">—</span>;
  if (typeof value === "number") return value.toLocaleString("de-CH", { maximumFractionDigits: 2 });
  return String(value);
}

export default function AiBenchmarkCard() {
  const fileRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runBenchmark = async (file) => {
    setRunning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiClient.post("/admin/ai-providers/benchmark", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(res.data);
      toast.success(`Benchmark fertig in ${res.data.providers?.reduce((m, p) => Math.max(m, p.latency_ms), 0)} ms`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Benchmark fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) runBenchmark(f);
    e.target.value = "";
  };

  // Bester Provider: erfolgreich + niedrigste Latenz
  const winner =
    result?.providers
      ?.filter((p) => p.success)
      ?.reduce((best, p) => (!best || p.latency_ms < best.latency_ms ? p : best), null) || null;

  const fields = Object.keys(FIELD_LABELS);
  const providers = result?.providers || [];

  return (
    <Card className="p-5 border-slate-200" data-testid="ai-benchmark-card">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-violet-100 flex items-center justify-center flex-shrink-0">
            <FlaskConical className="h-5 w-5 text-violet-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">AI-Benchmark</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Lade einen Beleg hoch und vergleiche Erkennungsgenauigkeit + Latenz aller Vision-LLMs parallel.
            </p>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleFile}
          data-testid="ai-benchmark-file-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={running}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="ai-benchmark-upload-btn"
        >
          {running ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Läuft …
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" /> Beleg hochladen & vergleichen
            </>
          )}
        </Button>
      </div>

      {!result && !running && (
        <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
          Lade einen Testbeleg hoch (JPG/PNG/PDF). Alle 3 konfigurierten Provider werden parallel angefragt.
        </div>
      )}

      {running && (
        <div className="text-center py-8 text-sm text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-violet-500" />
          Benchmark läuft (5–30s) …
        </div>
      )}

      {result && (
        <>
          {/* Winner Banner */}
          {winner && (
            <div className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              <Trophy className="h-4 w-4 text-emerald-700" />
              <span className="text-xs text-emerald-900">
                <span className="font-semibold">Schnellster:</span>{" "}
                {PROVIDER_LABELS[winner.provider] || winner.provider}
                {winner.model && <span className="text-slate-500"> ({winner.model})</span>}
                {" "}— {winner.latency_ms} ms
              </span>
            </div>
          )}

          {/* Comparison Table */}
          <div className="overflow-x-auto" data-testid="ai-benchmark-results-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Feld</th>
                  {providers.map((p) => (
                    <th key={p.provider} className="text-left py-2 px-2 font-medium text-slate-700">
                      <div className="flex items-center gap-1.5">
                        {p.success ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-rose-500" />
                        )}
                        {PROVIDER_LABELS[p.provider] || p.provider}
                      </div>
                      <div className="text-[10px] text-slate-400 font-normal">
                        {p.model || "—"} · {p.latency_ms}ms
                      </div>
                    </th>
                  ))}
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Konsens</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => {
                  const fieldVals = result.field_diff?.[f] || {};
                  const cons = result.consensus?.[f];
                  // Normalize for comparison
                  const norm = (v) => (v === null || v === undefined ? "" : String(v));
                  const consVal = cons?.value || "";
                  return (
                    <tr key={f} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-1.5 px-2 font-medium text-slate-600">{FIELD_LABELS[f]}</td>
                      {providers.map((p) => {
                        const v = fieldVals[p.provider];
                        const matchesConsensus = consVal && norm(v).toLowerCase() === consVal.toLowerCase();
                        return (
                          <td
                            key={p.provider}
                            className={`py-1.5 px-2 tabular-nums ${
                              matchesConsensus
                                ? "text-emerald-700"
                                : v
                                ? "text-amber-700"
                                : "text-slate-400"
                            }`}
                          >
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      <td className="py-1.5 px-2 text-slate-900 font-medium tabular-nums">
                        {cons ? (
                          <>
                            {fmtCell(cons.value)}
                            <span className="text-[10px] text-slate-400 ml-1">({cons.agreement})</span>
                          </>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Errors */}
          {providers.some((p) => !p.success) && (
            <div className="mt-3 space-y-1">
              {providers
                .filter((p) => !p.success)
                .map((p) => (
                  <div key={p.provider} className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                    <span className="font-semibold">{PROVIDER_LABELS[p.provider]}:</span> {p.error}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
